// Chart markers for "Something changed at this spot" (settings step 2c,
// docs/reference/zone_and_settings_changes.md §9). The zone keeps its history; the day of
// the change is drawn as a dashed line on the zone's time charts, so a before/after jump
// is read as the change it is.
//
// A marker is what GET /api_db/zone_markers/?zoneKey= returns: { MarkerDate: 'YYYY-MM-DD',
// Label: 'old → new' }. Axis keys are the chart's x values: 'YYYY-MM-DD' (daily) or
// 'YYYY-MM-DD HH:MM:SS' (hourly).

import { i18n, currentLanguage } from '@/i18n'
import { formatDate } from '@/i18n/format'

const tr = (key, params) => i18n.global.t(`zones.markers.${key}`, params || {})

export const MARKER_COLOR = '#4F7EB3'
// What a marker IS comes from its `Kind` (models.ZoneChangeMarker.to_dict), never from
// its Label: labels are stored English text, the kind is a code that survives translation.
//   spot            something changed at this spot; Label names it ("old → new")
//   spot_unnamed    the same, with nothing to name
//   detection       a camera box setting changed: no change AT the spot, but in how it is measured
//   counting_rules  the counting rules changed: idem
// The labels below are only the fallback for a marker from a backend without `Kind`.
export const DETECTION_LABEL = 'Detection settings changed'
export const COUNTING_RULES_LABEL = 'Counting rules changed'
const LEGACY_KIND = {
  'Changed at this spot': 'spot_unnamed',
  [DETECTION_LABEL]: 'detection',
  [COUNTING_RULES_LABEL]: 'counting_rules',
}
export const markerKind = (m) => (m && (m.Kind || LEGACY_KIND[m.Label])) || 'spot'
// The kinds that change HOW a spot is measured, each with its caption key.
const MEASURING = { detection: 'detection', counting_rules: 'countingRules' }

const dayOf = (key) => String(key).slice(0, 10)

// The markers that fall INSIDE the chart, each with the axis key its line goes on: the
// first key on or after the change day (a closed day has no key). A change on the first
// day or before it has no "before" in this period, so it is not drawn.
export function visibleMarkers(markers, axisKeys) {
  const keys = (axisKeys || []).filter(Boolean)
  if (!keys.length) return []
  const first = dayOf(keys[0])
  const last = dayOf(keys[keys.length - 1])
  const out = []
  for (const m of markers || []) {
    const d = m && m.MarkerDate
    if (!d || d <= first || d > last) continue
    const at = keys.find((k) => dayOf(k) >= d)
    if (at) out.push({ ...m, at })
  }
  return out
}

// The ECharts markLine for a series, or undefined when nothing falls inside the chart.
export function markLineFor(markers, axisKeys) {
  const shown = visibleMarkers(markers, axisKeys)
  if (!shown.length) return undefined
  return {
    silent: true,
    symbol: 'none',
    lineStyle: { color: MARKER_COLOR, type: 'dashed', width: 2 },
    label: { formatter: tr('changed'), position: 'end', color: MARKER_COLOR, fontSize: 10 },
    data: shown.map((m) => ({ xAxis: m.at })),
  }
}

// "12 Oct" / "12 okt" — dates the way people say them.
export const sayDay = (d) => (currentLanguage() === 'en'
  ? new Date(`${d}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
  : formatDate(d, { style: 'short' }))

// The line under the charts that says what the dashed line means (null when none shows).
export function markerCaption(markers, axisKeys) {
  const shown = visibleMarkers(markers, axisKeys)
  if (!shown.length) return null
  // The backend's label is "old → new"; with nothing to name it says "Changed at this spot".
  // A camera box setting is no change AT the spot but in how it is measured: said so.
  const said = (m) => m.Label && markerKind(m) === 'spot'
  const spot = shown.filter((m) => !MEASURING[markerKind(m)])
  const lines = []
  if (spot.length) {
    const parts = spot.map((m) => (said(m) ? `${sayDay(m.MarkerDate)} (${m.Label})` : sayDay(m.MarkerDate)))
    lines.push(tr('spot', { days: parts.join(` ${tr('and')} `) }))
  }
  for (const [kind, key] of Object.entries(MEASURING)) {
    const days = shown.filter((m) => markerKind(m) === kind).map((m) => sayDay(m.MarkerDate))
    if (days.length) lines.push(tr(key, { days: days.join(` ${tr('and')} `) }))
  }
  return tr('caption', { lines: lines.join('; ') })
}
